/**
 * Zoho PageSense analytics integration.
 *
 * Loads the PageSense tracking snippet at runtime. Loading is gated behind the
 * `config.features.analytics` feature flag so analytics can be disabled in
 * development and staging environments while remaining active in production.
 *
 * The snippet URL is project-specific and must not be modified (see Zoho docs:
 * https://help.zoho.com/portal/en/kb/pagesense/install-the-snippet/articles/manually-install-the-snippet-on-your-website).
 */
import { config } from '../config';

/** Project-specific PageSense snippet source (do not modify). */
const PAGESENSE_SRC =
  'https://cdn-eu.pagesense.io/js/copadata/92b47eca97814a36bd20ad5cbbf73e7d.js';

let initialized = false;

/**
 * Injects the Zoho PageSense snippet into the document head.
 *
 * Safe to call multiple times: the script is only injected once, and only when
 * the analytics feature flag is enabled.
 */
export function initAnalytics(): void {
  if (initialized) {
    return;
  }

  if (!config.features.analytics) {
    if (config.features.debug) {
      console.info('ℹ️ Analytics disabled (config.features.analytics is false)');
    }
    return;
  }

  if (typeof document === 'undefined') {
    return;
  }

  if (document.querySelector(`script[src="${PAGESENSE_SRC}"]`)) {
    initialized = true;
    return;
  }

  const script = document.createElement('script');
  script.src = PAGESENSE_SRC;
  script.async = true;
  document.head.appendChild(script);

  initialized = true;

  if (config.features.debug) {
    console.info('✅ Zoho PageSense analytics snippet loaded');
  }
}
